import LorentzClient from '../api/LorentzClient';
import {
  authenticateAgent,
  refreshAgentToken,
  parseJwtExpiry,
  LorentzSession
} from '../api/LorentzAuth';
import { buildScreenPopUrl } from '../utils/screenPop';
import { emit } from '../store/Events';
import { useStore } from '../store/Context';

let client = null;
let lastContactLookup = null;
let pendingQualification = null;
let powerDialingBusy = false;
let queueState = { count: 0, maxWait: 0 };
let refreshTimer = null;
let pauseAlertTimer = null;
let pauseActiveAt = null;
let pauseReasonId = null;
let recordingActive = false;
let parkedActive = false;
let predictiveBusy = false;
let predictiveTransferLock = false;
let lastLegacyEvent = null;

const URA_EXTENSION_STATES = new Set([
  'ictiLIN_URAT',
  'URAT',
  '48',
  48,
  0x30
]);

const getConfig = () => {
  const { lorentz = {} } = useStore.getState();
  return lorentz;
};

const ensureClient = () => {
  const cfg = getConfig();
  if (!cfg.enabled || !cfg.apiBaseUrl || !cfg.token) return null;

  if (
    !client ||
    client.apiBaseUrl !== cfg.apiBaseUrl ||
    client.agentId !== cfg.agentId
  ) {
    client = new LorentzClient({
      apiBaseUrl: cfg.apiBaseUrl,
      token: cfg.token,
      agentId: cfg.agentId,
      groupId: cfg.groupId,
      extension: cfg.extension,
      groupName: cfg.groupName
    });
  } else {
    client.setToken(cfg.token);
    client.setAgentContext({
      agentId: cfg.agentId,
      groupId: cfg.groupId,
      extension: cfg.extension,
      groupName: cfg.groupName
    });
  }

  return client;
};

const emitScreenPop = (data) => {
  const cfg = getConfig();
  const url = buildScreenPopUrl(cfg.screenPopUrl, {
    phone: data.phone,
    callId: data.context?.call_id || data.call_id,
    contactName: data.contact?.name,
    cpf: data.contact?.cpf || data.context?.cpf,
    campaignId: data.context?.campaign || data.campaign,
    agentId: cfg.agentId,
    groupId: cfg.groupId,
    extension: cfg.extension
  });

  emit({
    type: 'lorentz:contact:found',
    data: { ...data, screenPopUrl: url }
  });

  if (url) {
    emit({ type: 'lorentz:screen-pop:open', data: { url, ...data } });
  }
};

const parseLegacyPacket = legacy => {
  if (!legacy) return null;

  if (Array.isArray(legacy)) {
    return {
      event: Number(legacy[0] ?? legacy[1]),
      callId: legacy[2],
      contact: legacy[3],
      label: legacy[4],
      campaign: legacy[5],
      os: legacy[6],
      phone: legacy[7],
      cpf: legacy[8]
    };
  }

  if (typeof legacy === 'object') {
    return {
      event: Number(legacy.event ?? legacy.type ?? legacy.code ?? legacy[0]),
      callId: legacy.call_id ?? legacy.callId ?? legacy.atendimento,
      contact: legacy.contact ?? legacy.contato ?? legacy.assinante,
      label: legacy.label ?? legacy.servico,
      campaign: legacy.campaign ?? legacy.campanha,
      os: legacy.os ?? legacy.order_id,
      phone: legacy.phone ?? legacy.fone ?? legacy.telefone,
      cpf: legacy.cpf
    };
  }

  return null;
};

const isUraTransferState = state =>
  URA_EXTENSION_STATES.has(state?.extension_state) ||
  URA_EXTENSION_STATES.has(state?.extensionState) ||
  URA_EXTENSION_STATES.has(state?.ramal_state);

/**
 * Orquestra eventos do 008 → LorentzServer Web Agent API.
 */
export const LorentzService = {
  init() {
    const cfg = getConfig();
    if (!cfg.enabled) return;

    ensureClient();
    console.log('[Lorentz] integração Supervisor ativa', cfg.apiBaseUrl);

    this._bootstrapCti().catch(err =>
      console.warn('[Lorentz] bootstrap CTI', err)
    );

    if (cfg.pollIntervalMs > 0) {
      this._startPoll(cfg.pollIntervalMs);
    }

    this._startTokenRefresh();
    this._loadPauseReasons().catch(() => {});
    this._startPauseAlertMonitor();
  },

  async _bootstrapCti() {
    const api = ensureClient();
    if (!api) return;

    const cfg = getConfig();
    await api.register();
    if (cfg.ctiLogin !== false) {
      const login = await api.login({
        group: cfg.groupName,
        agentLogin: cfg.username
      });
      emit({ type: 'lorentz:cti:login', data: login });
    }
  },

  _pollTimer: null,
  _queueTimer: null,

  _startPoll(intervalMs) {
    if (this._pollTimer) clearInterval(this._pollTimer);
    this._pollTimer = setInterval(() => this._pollCti(), intervalMs);

    if (this._queueTimer) clearInterval(this._queueTimer);
    this._queueTimer = setInterval(() => this._pollQueue(), Math.max(intervalMs, 3000));
  },

  async _pollQueue() {
    const api = ensureClient();
    if (!api) return;

    try {
      const q = await api.getQueue();
      const count = Number(q?.count ?? q?.waiting ?? q?.queue_count ?? 0);
      const maxWait = Number(q?.max_wait ?? q?.maxWait ?? q?.longest_wait ?? 0);

      if (count !== queueState.count || maxWait !== queueState.maxWait) {
        queueState = { count, maxWait };
        emit({ type: 'lorentz:queue:update', data: queueState });
      }
    } catch (err) {
      console.debug('[Lorentz] queue', err.message);
    }
  },

  getQueueState() {
    return { ...queueState };
  },

  _startTokenRefresh() {
    if (refreshTimer) clearInterval(refreshTimer);

    refreshTimer = setInterval(async () => {
      const cfg = getConfig();
      if (!cfg.enabled || !cfg.token || !cfg.username) return;

      const expiresAt = cfg.tokenExpiresAt || parseJwtExpiry(cfg.token);
      if (!expiresAt) return;

      const msLeft = expiresAt - Date.now();
      if (msLeft > 5 * 60 * 1000) return;

      const password = LorentzSession.getPassword();
      if (!password) return;

      try {
        const auth = await refreshAgentToken({
          apiBaseUrl: cfg.apiBaseUrl,
          username: cfg.username,
          password,
          group: cfg.groupName
        });

        useStore.getState().setSettings({
          lorentz: {
            ...cfg,
            token: auth.token,
            tokenExpiresAt: auth.expiresAt
          }
        });

        if (client) client.setToken(auth.token);
        console.log('[Lorentz] JWT renovado');
      } catch (err) {
        console.warn('[Lorentz] falha ao renovar JWT', err.message);
        emit({
          type: 'lorentz:auth:expired',
          data: { message: err.message }
        });
      }
    }, 60 * 1000);
  },

  async _pollCti() {
    const api = ensureClient();
    if (!api) return;

    try {
      const state = await api.poll({ writeState: true });
      if (!state?.command && !state?.legacy && !state?.extension_state) return;

      if (state.legacy) {
        await this._handleLegacyPacket(state);
      }

      if (isUraTransferState(state)) {
        await this._handlePredictiveUraState(api, state);
      }

      if (state.command === 'RING' && state.phone) {
        predictiveBusy = false;
        emit({ type: 'lorentz:predictive:cleared' });
        emit({
          type: 'lorentz:cti:ring',
          data: state
        });
        if (state.contact) {
          emitScreenPop({ phone: state.phone, contact: state.contact, context: state });
        } else if (state.cpf) {
          emitScreenPop({
            phone: state.phone,
            contact: { cpf: state.cpf },
            context: state
          });
        }
      }

      if (state.command === 'DIAL' && state.phone) {
        emit({ type: 'lorentz:cti:dial', data: state });
        if (state.contact) {
          emitScreenPop({ phone: state.phone, contact: state.contact, context: state });
        }
      }

      if (state.command === 'IDLE') {
        await this._tryPowerDialing(api, state);
      }
    } catch (err) {
      console.debug('[Lorentz] poll', err.message);
    }
  },

  async _handleLegacyPacket(state) {
    const legacy = parseLegacyPacket(state.legacy);
    if (!legacy?.event) return;

    lastLegacyEvent = legacy;
    const cfg = getConfig();
    const phone = legacy.phone || state.phone;
    const contact = {
      name: legacy.contact,
      cpf: legacy.cpf,
      phone
    };
    const context = {
      call_id: legacy.callId,
      cpf: legacy.cpf,
      campaign: legacy.campaign,
      os: legacy.os,
      ...state
    };

    if (legacy.event === 131 || legacy.event === 123) {
      if (legacy.event === 131) {
        predictiveBusy = true;
        predictiveTransferLock = false;
        emit({
          type: 'lorentz:predictive:busy',
          data: {
            callId: legacy.callId,
            contact: legacy.contact,
            phone,
            cpf: legacy.cpf,
            campaign: legacy.campaign
          }
        });
      }

      if (phone || legacy.cpf || legacy.callId) {
        emitScreenPop({ phone, contact, context });
      }

      emit({
        type: 'lorentz:legacy:event',
        data: { event: legacy.event, legacy, state }
      });
    }

    if (legacy.event === 129 && cfg.supervisorEmbed) {
      emit({
        type: 'lorentz:supervisor:open-panel',
        data: { panel: 'perdas', count: legacy.phone }
      });
    }
  },

  async _handlePredictiveUraState(api, state) {
    const cfg = getConfig();
    if (!cfg.predictiveEnabled || !cfg.predictiveUraExtension) return;
    if (!predictiveBusy || predictiveTransferLock) return;

    const label = String(state.message || state.status || state.pause_state || '');
    if (label.toUpperCase().includes('OCUPADO') || lastLegacyEvent?.event === 131) {
      predictiveTransferLock = true;
      try {
        await api.transfer(String(cfg.predictiveUraExtension));
        emit({
          type: 'lorentz:predictive:transfer',
          data: {
            destination: cfg.predictiveUraExtension,
            legacy: lastLegacyEvent
          }
        });
      } catch (err) {
        predictiveTransferLock = false;
        console.warn('[Lorentz] transfer preditivo URA', err.message);
      }
    }
  },

  async _tryPowerDialing(api, state) {
    const cfg = getConfig();
    if (!cfg.powerDialing || powerDialingBusy) return;

    const pause = state?.pause_state;
    if (pause && !['PAUSA DESLIGADA', 'PAUSA DESATIVADA', 'PAUSA LOGIN'].includes(pause)) {
      return;
    }

    powerDialingBusy = true;
    try {
      const result = await api.powerDialing();
      if (!result?.available || !result?.dial?.phone) return;

      emit({ type: 'lorentz:power-dialing', data: result });

      if (cfg.autoDialPowerDialing !== false) {
        await api.dial(result.dial.phone);
        emit({
          type: 'lorentz:cti:dial',
          data: {
            command: 'DIAL',
            phone: result.dial.phone,
            contact: result.dial.contact
              ? { name: result.dial.contact, phone: result.dial.phone }
              : undefined,
            campaign: result.dial.campaign
          }
        });
      }
    } catch (err) {
      console.debug('[Lorentz] power-dialing', err.message);
    } finally {
      powerDialingBusy = false;
    }
  },

  async _loadPauseReasons() {
    const api = ensureClient();
    if (!api) return;

    const reasons = await api.listPauseReasons();
    if (!reasons?.length) return;

    useStore.getState().setSettings({
      lorentzPauseReasons: reasons
    });
  },

  reset() {
    if (this._pollTimer) {
      clearInterval(this._pollTimer);
      this._pollTimer = null;
    }
    if (this._queueTimer) {
      clearInterval(this._queueTimer);
      this._queueTimer = null;
    }
    if (refreshTimer) {
      clearInterval(refreshTimer);
      refreshTimer = null;
    }
    if (pauseAlertTimer) {
      clearInterval(pauseAlertTimer);
      pauseAlertTimer = null;
    }
    client = null;
    lastContactLookup = null;
    pendingQualification = null;
    powerDialingBusy = false;
    queueState = { count: 0, maxWait: 0 };
    pauseActiveAt = null;
    pauseReasonId = null;
    recordingActive = false;
    parkedActive = false;
    predictiveBusy = false;
    predictiveTransferLock = false;
    lastLegacyEvent = null;
  },

  /**
   * Mapeia status do softphone → pausa CTI.
   * statuses[].lorentzPauseReasonId no JSON de config.
   */
  async onStatusChange(status, options = {}) {
    const api = ensureClient();
    if (!api) return;

    const cfg = getConfig();
    const statusDef = (useStore.getState().statuses || []).find(
      s => s.value === status
    );
    const reasonId =
      options.reasonId ??
      statusDef?.lorentzPauseReasonId ??
      cfg.statusMap?.[status]?.pauseReasonId;

    try {
      if (status === 'online') {
        pauseActiveAt = null;
        pauseReasonId = null;
        emit({ type: 'lorentz:pause:cleared', data: { status } });
        await api.unpause();
        return;
      }

      if (reasonId) {
        pauseActiveAt = Date.now();
        pauseReasonId = reasonId;
        await api.pauseProductive({ active: true, reasonId });
        emit({
          type: 'lorentz:pause:active',
          data: { status, reasonId, productive: true }
        });
        return;
      }

      if (status === 'away' || status === 'offline') {
        pauseActiveAt = Date.now();
        pauseReasonId = null;
        await api.pauseEventual('activate');
        emit({ type: 'lorentz:pause:active', data: { status, productive: false } });
      }
    } catch (err) {
      console.error('[Lorentz] erro ao sincronizar pausa', err);
      emit({ type: 'lorentz:error', data: { action: 'pause', message: err.message } });
    }
  },

  /** Consulta contato ao receber chamada (screen pop). */
  async onPhoneRinging({ cdr }) {
    const api = ensureClient();
    if (!api || !cdr?.from) return;

    const cfg = getConfig();
    if (cfg.syncContactOnRinging === false) return;

    try {
      const phone = cdr.from;
      const [contact, context] = await Promise.all([
        api.lookupContact(phone),
        api.getCallContext({ callId: cdr.id, phone })
      ]);

      lastContactLookup = { phone, contact, context, at: Date.now() };

      emitScreenPop({ phone, contact, context, cdrId: cdr.id });

      if (contact?.name && cdr.contact) {
        cdr.contact.name = contact.name;
      }

      if (cfg.ctiAnswerOnRing === true) {
        await api.answer().catch(() => {});
      }
    } catch (err) {
      console.error('[Lorentz] lookup contato', err);
      emit({
        type: 'lorentz:contact:notfound',
        data: { phone: cdr.from, message: err.message }
      });
    }
  },

  /** Dispara fluxo de qualificação ao encerrar chamada. */
  async onPhoneTerminated({ cdr }) {
    const api = ensureClient();
    const cfg = getConfig();

    if (recordingActive && api) {
      await this.stopRecording().catch(() => {});
    }

    if (!api || !cfg.qualifyOnHangup) return;

    const callId = cdr?.id || cdr?.callId || lastContactLookup?.context?.call_id;
    if (!callId) return;

    try {
      const form = await api.getQualificationForm(callId, cdr?.from);
      pendingQualification = { callId, phone: cdr?.from, form };

      emit({
        type: 'lorentz:qualification:required',
        data: {
          callId,
          phone: cdr?.from,
          form,
          qualifications: form?.qualifications ?? [],
          mandatory: form?.mandatory ?? false
        }
      });

      const qualPauseId = cfg.qualificationPauseReasonId;
      if (qualPauseId) {
        await api.pauseProductive({ active: true, reasonId: qualPauseId });
      }
    } catch (err) {
      console.error('[Lorentz] qualificação', err);
      emit({
        type: 'lorentz:error',
        data: { action: 'qualification', message: err.message }
      });
    }
  },

  async submitQualification({ callId, qualificationId, phone, companions }) {
    const api = ensureClient();
    if (!api) throw new Error('Lorentz API não configurada');

    const id = callId || pendingQualification?.callId;
    const result = await api.submitQualification(id, {
      qualificationId,
      phone: phone || pendingQualification?.phone,
      companions
    });

    pendingQualification = null;
    await api.unpause().catch(() => {});

    emit({ type: 'lorentz:qualification:submitted', data: { callId: id, result } });
    return result;
  },

  async listPauseReasons() {
    const api = ensureClient();
    if (!api) return [];
    return api.listPauseReasons();
  },

  /** Fase 2 — transferência via CTI (central), não SIP refer. */
  async transferTo(destination) {
    const api = ensureClient();
    if (!api) throw new Error('Lorentz API não configurada');
    const dest = String(destination || '').trim();
    if (!dest) throw new Error('Informe o ramal ou grupo de destino');

    const result = await api.transfer(dest);
    emit({ type: 'lorentz:transfer:done', data: { destination: dest, result } });
    return result;
  },

  /** Fase 2 — captura chamada da fila (* no PhoenixCliente). */
  async captureCall() {
    const api = ensureClient();
    if (!api) throw new Error('Lorentz API não configurada');

    const result = await api.capture();
    emit({ type: 'lorentz:capture:done', data: result });
    return result;
  },

  /**
   * Fase 2 — retenção CTI (park) sincronizada com hold SIP.
   * ctiSyncHold no JSON (default true).
   */
  async syncHold(active) {
    const cfg = getConfig();
    if (!cfg.enabled || cfg.ctiSyncHold === false) return;

    const api = ensureClient();
    if (!api) return;

    try {
      if (active) {
        await api.park({ retrieve: false });
        parkedActive = true;
      } else {
        await api.park({ retrieve: true });
        parkedActive = false;
      }
      emit({ type: 'lorentz:park:changed', data: { active: parkedActive } });
    } catch (err) {
      console.warn('[Lorentz] park/hold', err.message);
      emit({ type: 'lorentz:error', data: { action: 'park', message: err.message } });
    }
  },

  isRecording() {
    return recordingActive;
  },

  isParked() {
    return parkedActive;
  },

  /** Fase 2 — gravação CTI (servidor), indicador na UI. */
  async startRecording() {
    const api = ensureClient();
    if (!api) throw new Error('Lorentz API não configurada');

    await api.startRecording();
    recordingActive = true;
    emit({ type: 'lorentz:recording:changed', data: { active: true } });
  },

  async stopRecording() {
    const api = ensureClient();
    if (!api) throw new Error('Lorentz API não configurada');

    const ctx = lastContactLookup?.context || {};
    await api.endRecording({
      callId: ctx.call_id,
      startedAt: ctx.recording_started_at
    });
    recordingActive = false;
    emit({ type: 'lorentz:recording:changed', data: { active: false } });
  },

  async toggleRecording() {
    if (recordingActive) return this.stopRecording();
    return this.startRecording();
  },

  async refreshRecordingState() {
    const api = ensureClient();
    if (!api) return false;

    try {
      const state = await api.getRecordingState();
      recordingActive = !!(state?.active ?? state?.recording);
      emit({ type: 'lorentz:recording:changed', data: { active: recordingActive } });
      return recordingActive;
    } catch {
      return recordingActive;
    }
  },

  _startPauseAlertMonitor() {
    if (pauseAlertTimer) clearInterval(pauseAlertTimer);

    pauseAlertTimer = setInterval(() => {
      const cfg = getConfig();
      if (!cfg.enabled || !pauseActiveAt) return;

      const reasons = useStore.getState().lorentzPauseReasons || [];
      const reason = reasons.find(
        r => Number(r.id ?? r.cod ?? r.Cod) === Number(pauseReasonId)
      );

      const alertSec = Number(
        reason?.tempoAlerta ?? reason?.TempoAlerta ?? reason?.tempo_alerta ?? 0
      );
      const tolSec = Number(
        reason?.tempoTolerancia ??
          reason?.TempoTolerancia ??
          reason?.tempo_tolerancia ??
          0
      );

      if (alertSec <= 0 && tolSec <= 0) return;

      const elapsed = Math.floor((Date.now() - pauseActiveAt) / 1000);
      const limit = alertSec + tolSec;

      if (elapsed >= limit) {
        emit({
          type: 'lorentz:pause:exceeded',
          data: {
            elapsed,
            limit,
            reasonId: pauseReasonId,
            description: reason?.desc ?? reason?.Desc ?? reason?.name
          }
        });
      } else if (elapsed >= alertSec && alertSec > 0) {
        emit({
          type: 'lorentz:pause:warning',
          data: {
            elapsed,
            alertSec,
            limit,
            reasonId: pauseReasonId,
            description: reason?.desc ?? reason?.Desc ?? reason?.name
          }
        });
      }
    }, 10 * 1000);
  },

  async logout() {
    const api = ensureClient();
    if (!api) return;
    await api.logout();
    LorentzSession.clear();
    this.reset();
    emit({ type: 'lorentz:cti:logout' });
  },

  async handleInboundEvent(type, payload) {
    switch (type) {
      case 'lorentz:pause':
        return this.onStatusChange(payload?.status || 'away', payload);
      case 'lorentz:qualify':
        return this.submitQualification(payload);
      case 'lorentz:lookup':
        return this.onPhoneRinging({ cdr: { from: payload?.phone, id: payload?.callId } });
      case 'lorentz:unpause':
        return ensureClient()?.unpause();
      case 'lorentz:transfer':
        return this.transferTo(payload?.destination || payload?.phone);
      case 'lorentz:capture':
        return this.captureCall();
      case 'lorentz:recording':
        return payload?.active === false
          ? this.stopRecording()
          : this.startRecording();
      case 'lorentz:logout':
        return this.logout();
      case 'lorentz:supervisor:dial':
        return ensureClient()?.dial(payload?.phone || payload?.destination);
      default:
        break;
    }
  },

  handlePhoneEvent(type, data) {
    if (type === 'phone:ringing') return this.onPhoneRinging(data);
    if (type === 'phone:terminated') return this.onPhoneTerminated(data);
  }
};

export default LorentzService;
